import { Box, Container, Paper, Typography, useTheme } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import { useState, useEffect } from 'react';

export default function HelpPage() {
  const [markdownContent, setMarkdownContent] = useState<string>('');
  const theme = useTheme();

  useEffect(() => {
    // In a real application, this could be loaded from an API or a local file
    const helpContent = `
## Overview
FogCast provides real-time fog visibility forecasts for airports, helping you plan ahead with hours of advance warning.

## How to Use
- View the timeline to see current and upcoming fog conditions
- Each coloured segment represents predicted visibility conditions
- Hover or tap a segment to see details

## Visibility Levels
- 🟢 **Green**: Good visibility (>3000m)
- 🟡 **Yellow**: Partial visibility (1600m≥3000m)
- 🟠 **Orange**: Moderate visibility (800m≥1600m)
- 🔴 **Red**: Poor visibility/Fog (<800m)

## Time Controls
By default the timelines will be refreshed every 5 minutes. The refresh button can be used to update forecasts manually. Use the **Adjust Date** button to view historical forecasts, and the **timezone toggle** to switch between UTC and local time.

## Warning / Error States
These will be highlighted by a callout banner at the top of the page. The timelines impacted will also carry a warning sign.

- ❓ **No Data**: No data is available for one or more time segments
- 🕒 **Delayed Forecast**: The forecast is delayed and may not be accurate due to out of date weather data
- ⛔️ **Error**: An error occurred while fetching the data.

In the above cases proceed with caution as the forecast performance will be degraded. 
In certain cases the forecast may not be displayed at all due to the risk of misleading or incorrect visibility forecasts.

---

## System Status
Check the current system status and service availability at [status.pypervision.com](https://status.pypervision.com)

---

## Data Protection & Privacy

Pyper Vision Ltd manages the personal data collected through FogCast.

### What we collect
- **Account information**: Name and email address (provided via your login)
- **Usage data**: Activity timestamps, notification preferences, and administrative action history
- **Feedback**: Optional phone number and feedback text if you choose to submit it
- **Authentication**: Managed securely by Auth0, a trusted industry-standard identity provider — your passwords are never stored in FogCast
- **Error diagnostics**: Technical data such as browser type and error details, used to identify and fix issues

### Why we collect it
To provide and improve the FogCast fog forecasting service, manage user access, and respond to support requests.

### Data retention
We retain your personal data for as long as your account is active. If you request account deletion, we will remove your data.

### Your rights
We respect your data rights. You can:
- **Access** — request a copy of the personal data we hold about you
- **Rectification** — request correction of inaccurate data
- **Erasure** — request deletion of your personal data (right to be forgotten)
- **Restrict processing** — request that we limit how we use your data
- **Data portability** — request your data in a portable format

### How to exercise your rights
Email [support@pypervision.com](mailto:support@pypervision.com) with your request. We will respond within 30 days.

### Cookies & browser storage
FogCast does not use cookies for tracking or advertising. Authentication tokens are held in memory only and are not stored in your browser. Auth0 may set strictly necessary cookies during the login process to keep you signed in.

### Third parties
- **Auth0** — authentication and login
- **Sentry** — error monitoring and diagnostics

We do not sell or share your personal data with any other third party.

---

## Questions?
- **24/7 Phone Support**: +64 9 888 0558
- **Email**: [support@pypervision.com](mailto:support@pypervision.com)
`;
    
    setMarkdownContent(helpContent);
  }, []);

  return (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
      <Box sx={{ my: { xs: 3, md: 6 } }}>
        <Typography 
          variant="h2" 
          component="h1" 
          gutterBottom 
          align="center"
          sx={{ 
            mb: { xs: 2.5, md: 4 },
            fontWeight: 700,
            fontSize: { xs: '1.75rem', sm: '2.5rem', md: '3rem' },
            background: 'linear-gradient(45deg, #4dd0e1 30%, #80deea 90%)',
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent'
          }}
        >
          Help & Documentation
        </Typography>
        
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 2, sm: 3, md: 5 }, 
            borderRadius: { xs: 1, md: 2 },
            bgcolor: '#ffffff',
            color: 'rgba(33, 33, 33, 0.87)',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)'
          }}
        >
          <Box className="markdown-content" sx={{
            '& h1': { 
              fontSize: { xs: '1.7rem', sm: '2rem', md: '2.2rem' }, 
              fontWeight: 700, 
              color: theme.palette.primary.main,
              marginTop: { xs: 2, md: 3 }, 
              marginBottom: { xs: 2, md: 3 },
              paddingBottom: 1,
              borderBottom: `1px solid ${theme.palette.divider}` 
            },
            '& h2': { 
              fontSize: { xs: '1.4rem', sm: '1.6rem', md: '1.8rem' }, 
              fontWeight: 600, 
              color: 'rgba(0, 0, 0, 0.8)',
              marginTop: { xs: 2.5, md: 4 }, 
              marginBottom: { xs: 1.5, md: 2 },
              position: 'relative',
              '&::before': {
                content: '""',
                position: 'absolute',
                left: -8,
                top: '50%',
                transform: 'translateY(-50%)',
                width: 4,
                height: '70%',
                backgroundColor: theme.palette.primary.main,
                borderRadius: 4,
                display: { xs: 'none', sm: 'block' }
              }
            },
            '& h3': { 
              fontSize: { xs: '1.2rem', sm: '1.35rem', md: '1.5rem' }, 
              fontWeight: 600, 
              color: 'rgba(0, 0, 0, 0.75)',
              marginTop: { xs: 2, md: 3 }, 
              marginBottom: { xs: 1, md: 1.5 } 
            },
            '& p': { 
              marginBottom: { xs: 1.5, md: 2 },
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: 1.6,
              color: 'rgba(0, 0, 0, 0.7)'
            },
            '& ul, & ol': { 
              paddingLeft: { xs: 2, sm: 3 }, 
              marginBottom: { xs: 2, md: 3 },
              fontSize: { xs: '0.95rem', sm: '1rem' },
              lineHeight: 1.6
            },
            '& li': { 
              marginBottom: { xs: 1, md: 1.5 },
              position: 'relative',
              paddingLeft: 0.5
            },
            '& strong': { 
              fontWeight: 700,
              color: 'rgba(0, 0, 0, 0.8)'
            },
            '& em': { 
              fontStyle: 'italic',
              color: 'rgba(0, 0, 0, 0.75)'
            },
            '& a': {
              color: theme.palette.primary.main,
              textDecoration: 'none',
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline'
              }
            },
            '& img': {
              display: 'block',
              marginLeft: 'auto',
              marginRight: 'auto',
              maxWidth: '100%',
              height: 'auto',
              borderRadius: 2,
              boxShadow: '0 2px 12px rgba(0,0,0,0.08)',
              marginTop: 2,
              marginBottom: 2,
            },
          }}>
            <ReactMarkdown>
              {markdownContent}
            </ReactMarkdown>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
} 